import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import {
  showErrorAlert,
  showActualizadoCorrectamenteAlert,
  showUpdateCreditoAlert,
} from "../../../components/molecules/ShowAlert";
import { actualizarPersona } from "../../../services/persona.service";
import {
  actualizarEstudiante,
  getOneEstudiante,
} from "../../../services/estudiante.service";
import { getAllProvinciasByIdDepartamento } from "../../../services/provincia.service";
import { getAllDepartamentosByIdPais } from "../../../services/departamento.service";
import { getAllTipoAdmisiones } from "../../../services/tipoAdmision.service";
import { getAllPaises } from "../../../services/pais.service";
import { generosPersona, TIPO_ADMISION_B } from "../../../utils/constantes";
import { getAllIdiomasByIdEstudiante, getAllIdiomas } from "../../../services/idioma.service";
import { getAllNivelesByIdIdioma } from "../../../services/nivel.service";
import { getAllModulosByIdNivelAndIdIdioma } from "../../../services/modulo.service";
import { getAllProgramasByIdIdioma } from "../../../services/programa.service";
import { crearInscripcion, getAllInscripciones } from "../../../services/inscripcion.service";
import { crearEleccionIdioma } from "../../../services/eleccionIdioma";
import { showAlert } from "../../../components/molecules/ShowAlert";

const inputStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  marginBottom: "20px",
  fontSize: "16px",
  transition: "all 0.3s ease",
};

const formSectionStyle = {
  marginBottom: "30px",
  padding: "25px",
  borderRadius: "12px",
  background: "#ffffff",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
};

const buttonStyle = {
  padding: "12px 24px",
  fontSize: "16px",
  fontWeight: "600",
  borderRadius: "8px",
  cursor: "pointer",
  border: "none",
  transition: "all 0.3s ease",
};

const saveButtonStyle = {
  ...buttonStyle,
  backgroundColor: "#4c51bf",
  color: "#fff",
  boxShadow: "0 4px 15px rgba(76, 81, 191, 0.3)",
};

const cancelButtonStyle = {
  ...buttonStyle,
  backgroundColor: "#e53e3e",
  color: "#fff",
  boxShadow: "0 4px 15px rgba(229, 62, 62, 0.3)",
};

const validationSchema = yup.object().shape({
  nombre: yup.string().required("El nombre es requerido."),
  apellido: yup.string().required("El apellido es requerido."),
  ci: yup.string().optional(),
  celular: yup.string().required("El celular es requerido."),
  correo: yup.string().email("Debe ser un correo válido.").optional(),
  idDepartamento: yup.string().required("El departamento es requerido."),
  idProvincia: yup.string().optional(),
  fechaNacimiento: yup.string().optional(),
  sexo: yup.string().required("El sexo es requerido."),
  anioAdmision: yup.string().optional(),
  organizacionSocial: yup.string().optional(),
  credito: yup.number().required("El crédito es requerido."),
  idTipoAdmision: yup.string().required("El tipo de admisión es requerido."),
});

export const ActualizarEstudiantePage = () => {
  const { idEstudiante } = useParams();
  const navigate = useNavigate();

  const [departamentos, setDepartamentos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [tiposAdmision, setTiposAdmision] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [idiomasEstudiante, setIdiomasEstudiante] = useState([]);
  
  // Estados para agregar idioma
  const [mostrarAgregarIdioma, setMostrarAgregarIdioma] = useState(false);
  const [idiomasDisponibles, setIdiomasDisponibles] = useState([]);
  const [niveles, setNiveles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [idIdiomaSeleccionado, setIdIdiomaSeleccionado] = useState(null);
  const [idNivelSeleccionado, setIdNivelSeleccionado] = useState(null);
  const [idModuloSeleccionado, setIdModuloSeleccionado] = useState(null);
  const [idProgramaSeleccionado, setIdProgramaSeleccionado] = useState(null);
  const [agregandoIdioma, setAgregandoIdioma] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const idDepartamento = watch("idDepartamento");
  const idTipoAdmision = watch("idTipoAdmision");
  const idProvincia = watch("idProvincia");

  // Registrar manualmente los campos controlados
  useEffect(() => {
    register("idDepartamento");
    register("idProvincia");
    register("idTipoAdmision");
  }, [register]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const estudiante = await getOneEstudiante(idEstudiante);

        if (!estudiante || !estudiante.persona) {
          throw new Error("El estudiante o su persona están vacíos o inválidos.");
        }

        const paises = await getAllPaises();
        
        // Obtener el país del departamento del estudiante si existe
        let idPaisParaCargar = null;
        if (estudiante.departamento && estudiante.departamento.idPais) {
          idPaisParaCargar = estudiante.departamento.idPais;
          console.log(`📍 Estudiante tiene departamento del país: ${estudiante.departamento.idPais}`);
        }
        
        // Si no tiene país en el departamento, cargar TODOS los departamentos de TODOS los países
        let departamentosList = [];
        if (idPaisParaCargar) {
          // Cargar solo del país del estudiante
          departamentosList = await getAllDepartamentosByIdPais(idPaisParaCargar);
        } else {
          // Cargar de TODOS los países (para que funcione con Perú, Bolivia, etc.)
          console.log('📦 Cargando departamentos de todos los países...');
          const todosDepartamentos = [];
          for (const pais of paises) {
            try {
              const dptos = await getAllDepartamentosByIdPais(pais.idPais);
              if (dptos && dptos.length > 0) {
                todosDepartamentos.push(...dptos);
              }
            } catch (err) {
              console.warn(`Error cargando departamentos de ${pais.nombre}:`, err);
            }
          }
          departamentosList = todosDepartamentos;
          console.log(`✅ Cargados ${departamentosList.length} departamentos de todos los países`);
        }
        
        const tiposAdmisionList = await getAllTipoAdmisiones();

        const provinciasList = await getAllProvinciasByIdDepartamento(
          estudiante.persona.idDepartamento
        );

        // Primero setear listas
        setDepartamentos(departamentosList);
        setTiposAdmision(tiposAdmisionList);
        setProvincias(provinciasList);

        // Luego setear los valores del formulario
        setValue("idPersona", estudiante.persona.idPersona);
        setValue("nombre", estudiante.persona.nombre);
        setValue("apellido", estudiante.persona.apellido);
        setValue("celular", estudiante.persona.celular);
        setValue("correo", estudiante.persona.correo);
        setValue("ci", estudiante.persona.ci);
        setValue(
          "fechaNacimiento",
          estudiante.persona.fechaNacimiento?.split("T")[0] || ""
        );
        setValue("idDepartamento", estudiante.persona.idDepartamento);
        setValue("idProvincia", estudiante.persona.idProvincia);
        setValue("sexo", estudiante.persona.sexo);
        setValue(
          "anioAdmision",
          estudiante.estudiante.anioAdmision?.split("T")[0] || ""
        );
        setValue(
          "organizacionSocial",
          estudiante.estudiante.organizacionSocial || ""
        );
        setValue("credito", estudiante.estudiante.credito);
        setValue("idTipoAdmision", estudiante.estudiante.idTipoAdmision);

        validarValoresSelect(
          departamentosList,
          tiposAdmisionList,
          estudiante,
          provinciasList
        );

        // Cargar idiomas del estudiante
        try {
          const idiomas = await getAllIdiomasByIdEstudiante(idEstudiante);
          setIdiomasEstudiante(idiomas || []);
        } catch (error) {
          console.error("❌ Error al cargar idiomas:", error);
          setIdiomasEstudiante([]);
        }

        // Cargar todos los idiomas disponibles para el formulario
        try {
          const todosIdiomas = await getAllIdiomas();
          setIdiomasDisponibles(todosIdiomas || []);
        } catch (error) {
          console.error("❌ Error al cargar idiomas disponibles:", error);
          setIdiomasDisponibles([]);
        }
      } catch (error) {
        console.error(" Error en fetchData:", error);
        showErrorAlert("Error al cargar los datos del estudiante.");
      }
    };

    fetchData();
  }, [idEstudiante, setValue]);

  const validarValoresSelect = (
    departamentosList,
    tiposAdmisionList,
    estudiante,
    provinciasList
  ) => {
    const idDep = estudiante?.persona?.idDepartamento;
    const idTipo = estudiante?.estudiante?.idTipoAdmision;
    const idProv = estudiante?.persona?.idProvincia;

    const existeDepartamento = departamentosList.some(
      (dep) => dep.idDepartamento === idDep
    );
    const existeTipoAdmision = tiposAdmisionList.some(
      (tipo) => tipo.idTipoAdmision === idTipo
    );
    const existeProvincia = provinciasList.some(
      (p) => p.idProvincia === idProv
    );

    if (!existeDepartamento) {
      console.warn("⚠️ El idDepartamento del estudiante NO existe en la lista:", idDep);
    } else {
      console.log("✅ idDepartamento válido:", idDep);
    }

    if (!existeTipoAdmision) {
      console.warn("⚠️ El idTipoAdmision del estudiante NO existe en la lista:", idTipo);
    } else {
      console.log("✅ idTipoAdmision válido:", idTipo);
    }

    if (!existeProvincia) {
      console.warn("⚠️ idProvincia del estudiante NO existe:", idProv);
    } else {
      console.log("✅ idProvincia válida:", idProv);
    }
  };

  useEffect(() => {
    if (idDepartamento) {
      const fetchProvincias = async () => {
        try {
          const provinciasList = await getAllProvinciasByIdDepartamento(
            idDepartamento
          );
          setProvincias(provinciasList);
          // Opcional: resetear la provincia si la anterior no está en la nueva lista
          if (!provinciasList.some(p => p.idProvincia === watch("idProvincia"))) {
            setValue("idProvincia", "");
          }
        } catch (error) {
          showErrorAlert("Error al cargar las provincias.");
        }
      };
      fetchProvincias();
    } else {
      setProvincias([]); // Limpiar provincias si no hay departamento seleccionado
    }
  }, [idDepartamento, setValue, watch]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const personaPayload = {
        idPersona: data.idPersona,
        nombre: data.nombre,
        apellido: data.apellido,
        celular: data.celular,
        correo: data.correo,
        ci: data.ci,
        fechaNacimiento: data.fechaNacimiento ? new Date(data.fechaNacimiento).toISOString() : null,
        idDepartamento: data.idDepartamento,
        idProvincia: data.idProvincia,
        sexo: data.sexo,
      };

      const estudiantePayload = {
        idEstudiante,
        anioAdmision: data.anioAdmision ? new Date(data.anioAdmision).toISOString() : null,
        organizacionSocial: data.organizacionSocial,
        credito: data.credito,
        idTipoAdmision: data.idTipoAdmision,
      };

      await actualizarPersona(data.idPersona, personaPayload);
      await actualizarEstudiante(idEstudiante, estudiantePayload);

      showActualizadoCorrectamenteAlert({
        text: "Los datos del estudiante han sido actualizados correctamente.",
        callback: () => {
          if (
            data.credito > 0 &&
            tiposAdmision.find((tipo) => tipo.idTipoAdmision === data.idTipoAdmision)?.nombre !== TIPO_ADMISION_B
          ) {
            showUpdateCreditoAlert({
              text: "Porque el estudiante está registrado como Regular. Si deseas modificar el crédito, cambia el tipo de admisión a Becario.",
              callback: () => navigate("/estudiantes"),
            });
          } else {
            navigate("/estudiantes");
          }
        },
      });
    } catch (error) {
      console.error("❌ Error al actualizar:", error);
      showErrorAlert("Error al actualizar los datos.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/estudiantes");
  };

  // Funciones para agregar idioma
  const handleAbrirAgregarIdioma = async () => {
    try {
      // Si no se han cargado los idiomas disponibles, cargarlos ahora
      if (!idiomasDisponibles || idiomasDisponibles.length === 0) {
        try {
          const todosIdiomas = await getAllIdiomas();
          setIdiomasDisponibles(todosIdiomas || []);
        } catch (error) {
          console.error("❌ Error al cargar idiomas disponibles:", error);
          showErrorAlert({ text: "Error al cargar los idiomas disponibles. Por favor intenta nuevamente." });
          return;
        }
      }
      
      // Abrir el modal
      setMostrarAgregarIdioma(true);
      // Resetear selecciones
      setIdIdiomaSeleccionado(null);
      setIdNivelSeleccionado(null);
      setIdModuloSeleccionado(null);
      setIdProgramaSeleccionado(null);
      setNiveles([]);
      setModulos([]);
      setProgramas([]);
    } catch (error) {
      console.error("❌ Error al abrir modal de agregar idioma:", error);
      showErrorAlert({ text: "Error al abrir el formulario. Por favor intenta nuevamente." });
    }
  };

  const handleCerrarAgregarIdioma = () => {
    setMostrarAgregarIdioma(false);
    setIdIdiomaSeleccionado(null);
    setIdNivelSeleccionado(null);
    setIdModuloSeleccionado(null);
    setIdProgramaSeleccionado(null);
    setNiveles([]);
    setModulos([]);
    setProgramas([]);
  };

  const handleSeleccionarIdioma = async (idIdioma) => {
    setIdIdiomaSeleccionado(idIdioma);
    setIdNivelSeleccionado(null);
    setIdModuloSeleccionado(null);
    setIdProgramaSeleccionado(null);
    setModulos([]);
    
    try {
      // Cargar niveles del idioma
      const nivelesData = await getAllNivelesByIdIdioma(idIdioma);
      setNiveles(nivelesData || []);
      
      // Cargar programas del idioma
      const programasData = await getAllProgramasByIdIdioma(idIdioma);
      setProgramas(programasData || []);
    } catch (error) {
      console.error("Error al cargar niveles/programas:", error);
      setNiveles([]);
      setProgramas([]);
    }
  };

  const handleSeleccionarNivel = async (idNivel) => {
    setIdNivelSeleccionado(idNivel);
    setIdModuloSeleccionado(null);
    
    if (!idIdiomaSeleccionado) return;
    
    try {
      const modulosData = await getAllModulosByIdNivelAndIdIdioma(idNivel, idIdiomaSeleccionado);
      setModulos(modulosData || []);
    } catch (error) {
      console.error("Error al cargar módulos:", error);
      setModulos([]);
    }
  };

  const handleAgregarIdioma = async () => {
    if (!idIdiomaSeleccionado || !idModuloSeleccionado) {
      showErrorAlert({ text: "Por favor selecciona un idioma y un módulo" });
      return;
    }

    setAgregandoIdioma(true);
    
    try {
      console.log("🔍 Verificando idiomas actuales del estudiante...");
      // Verificar si el estudiante ya tiene este idioma
      const idiomasActuales = await getAllIdiomasByIdEstudiante(idEstudiante);
      const yaTieneIdioma = idiomasActuales?.some(idioma => 
        String(idioma.idIdioma) === String(idIdiomaSeleccionado)
      );
      
      if (yaTieneIdioma) {
        showErrorAlert({ text: "Este estudiante ya tiene este idioma asignado" });
        setAgregandoIdioma(false);
        return;
      }

      console.log("🔍 Buscando inscripciones existentes...");
      // Obtener todas las inscripciones del estudiante para verificar si hay una existente
      const todasInscripciones = await getAllInscripciones().catch(err => {
        console.error("Error al obtener inscripciones:", err);
        return [];
      });
      
      // Normalizar IDs para comparación
      const idEstudianteStr = String(idEstudiante);
      let inscripcionExistente = todasInscripciones?.find(ins => {
        const insIdEstudiante = String(ins.idEstudiante || '');
        return insIdEstudiante === idEstudianteStr && (ins.habilitado === true || ins.habilitado === undefined);
      });

      console.log("📝 Inscripción existente:", inscripcionExistente);

      // Si no hay inscripción, crear una nueva
      if (!inscripcionExistente) {
        console.log("➕ Creando nueva inscripción...");
        inscripcionExistente = await crearInscripcion({
          idEstudiante: idEstudiante,
          idPrograma: idProgramaSeleccionado || null,
          descripcion: "Inscripción creada al agregar idioma",
          idCursoExtra: null
        });
        console.log("✅ Inscripción creada:", inscripcionExistente);
      }

      if (!inscripcionExistente || !inscripcionExistente.idInscripcion) {
        throw new Error("No se pudo obtener o crear la inscripción");
      }

      console.log("🌍 Creando elección de idioma...");
      // Crear la elección de idioma
      await crearEleccionIdioma({
        idInscripcion: inscripcionExistente.idInscripcion,
        idIdioma: idIdiomaSeleccionado,
        idModulo: idModuloSeleccionado
      });

      console.log("🔄 Actualizando lista de idiomas...");
      // Actualizar la lista de idiomas del estudiante
      const idiomasActualizados = await getAllIdiomasByIdEstudiante(idEstudiante);
      setIdiomasEstudiante(idiomasActualizados || []);

      showAlert({ text: "Idioma agregado correctamente" });
      handleCerrarAgregarIdioma();
    } catch (error) {
      console.error("❌ Error completo al agregar idioma:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Error message:", error.message);
      const mensajeError = error.response?.data?.message 
        || error.message 
        || "Error al agregar el idioma. Por favor intenta nuevamente.";
      showErrorAlert({ text: mensajeError });
    } finally {
      setAgregandoIdioma(false);
    }
  };

  return (
    <ContenedorPageTemplate titulo="Actualizar datos del Estudiante">
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          padding: "30px",
          background: "#f7fafc",
          borderRadius: "16px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
        }}
      >
        {/* Sección de Datos Personales */}
        <div style={formSectionStyle}>
          <h2
            style={{
              fontSize: "24px",
              marginBottom: "20px",
              color: "#2d3748",
              borderBottom: "2px solid #edf2f7",
              paddingBottom: "10px",
            }}
          >
            Datos Personales 👤
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            <div>
              <label htmlFor="nombre">Nombre:</label>
              <input id="nombre" style={inputStyle} {...register("nombre")} />
              {errors.nombre && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.nombre.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="apellido">Apellido:</label>
              <input id="apellido" style={inputStyle} {...register("apellido")} />
              {errors.apellido && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.apellido.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="ci">CI:</label>
              <input id="ci" style={inputStyle} {...register("ci")} />
              {errors.ci && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.ci.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="celular">Celular:</label>
              <input id="celular" style={inputStyle} {...register("celular")} />
              {errors.celular && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.celular.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="correo">Correo:</label>
              <input
                id="correo"
                type="email"
                style={inputStyle}
                {...register("correo")}
              />
              {errors.correo && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.correo.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="fechaNacimiento">Fecha de Nacimiento:</label>
              <input
                id="fechaNacimiento"
                type="date"
                style={inputStyle}
                {...register("fechaNacimiento")}
              />
              {errors.fechaNacimiento && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.fechaNacimiento.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="idDepartamento">Departamento:</label>
              <select
                id="idDepartamento"
                style={inputStyle}
                value={idDepartamento || ""}
                onChange={(e) => setValue("idDepartamento", e.target.value, { shouldValidate: true })}
              >
                <option value="">Selecciona un departamento</option>
                {departamentos.map((dep) => (
                  <option key={dep.idDepartamento} value={dep.idDepartamento}>
                    {dep.nombre}
                  </option>
                ))}
              </select>
              {errors.idDepartamento && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.idDepartamento.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="idProvincia">Provincia:</label>
              <select
                id="idProvincia"
                style={inputStyle}
                value={idProvincia || ""}
                disabled={!idDepartamento}
                onChange={(e) => setValue("idProvincia", e.target.value, { shouldValidate: true })}
              >
                <option value="">
                  {idDepartamento
                    ? "Selecciona una provincia"
                    : "Selecciona un departamento primero"}
                </option>
                {provincias.map((prov) => (
                  <option key={prov.idProvincia} value={prov.idProvincia}>
                    {prov.nombre}
                  </option>
                ))}
              </select>
              {errors.idProvincia && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.idProvincia.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="sexo">Sexo:</label>
              <select id="sexo" style={inputStyle} {...register("sexo")}>
                <option value="">Selecciona una opción</option>
                {generosPersona.map((genero) => (
                  <option key={genero.value} value={genero.value}>
                    {genero.label}
                  </option>
                ))}
              </select>
              {errors.sexo && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.sexo.message}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Sección de Datos Académicos */}
        <div style={formSectionStyle}>
          <h2
            style={{
              fontSize: "24px",
              marginBottom: "20px",
              color: "#2d3748",
              borderBottom: "2px solid #edf2f7",
              paddingBottom: "10px",
            }}
          >
            Datos Académicos 🎓
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            <div>
              <label htmlFor="anioAdmision">Fecha de Admisión:</label>
              <input
                id="anioAdmision"
                type="date"
                style={inputStyle}
                {...register("anioAdmision")}
              />
              {errors.anioAdmision && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.anioAdmision.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="organizacionSocial">Organización Social:</label>
              <input
                id="organizacionSocial"
                style={inputStyle}
                {...register("organizacionSocial")}
              />
              {errors.organizacionSocial && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.organizacionSocial.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="credito">Crédito:</label>
              <input
                id="credito"
                type="number"
                style={inputStyle}
                min="0"
                {...register("credito")}
              />
              {errors.credito && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.credito.message}
                </span>
              )}
            </div>

            <div>
              <label htmlFor="idTipoAdmision">Tipo de Admisión:</label>
              <select
                id="idTipoAdmision"
                style={inputStyle}
                value={idTipoAdmision || ""}
                onChange={(e) => setValue("idTipoAdmision", e.target.value, { shouldValidate: true })}
              >
                <option value="">Selecciona un tipo de admisión</option>
                {tiposAdmision.map((tipo) => (
                  <option
                    key={tipo.idTipoAdmision}
                    value={tipo.idTipoAdmision}
                  >
                    {tipo.nombre}
                  </option>
                ))}
              </select>
              {errors.idTipoAdmision && (
                <span style={{ color: "#e53e3e", fontSize: "14px" }}>
                  {errors.idTipoAdmision.message}
                </span>
              )}
            </div>
          </div>

          {/* Idiomas del Estudiante - Integrado en Datos Académicos */}
          <div style={{ marginTop: "30px" }}>
            <h3
              style={{
                fontSize: "20px",
                marginBottom: "20px",
                color: "#4a5568",
                borderBottom: "1px solid #e2e8f0",
                paddingBottom: "8px",
              }}
            >
              Idiomas del Estudiante 🌍
            </h3>
            
            {idiomasEstudiante && idiomasEstudiante.length > 0 ? (
              <>
                <div style={{ marginBottom: "16px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={handleAbrirAgregarIdioma}
                    style={{
                      padding: "8px 16px",
                      background: "#667eea",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "background 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#5a67d8";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#667eea";
                    }}
                  >
                    + Agregar Otro Idioma
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {idiomasEstudiante.map((idioma, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "14px",
                        background: "#ffffff",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "0 1px 4px rgba(0, 0, 0, 0.05)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          background: "linear-gradient(135deg, #667eea, #764ba2)",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "10px",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "14px",
                        }}
                      >
                        {(idioma?.nombre || 'I').charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: "0 0 3px 0", fontSize: "15px", fontWeight: "600", color: "#2d3748" }}>
                          {idioma?.nombre || 'Idioma'}
                        </h4>
                        <p style={{ margin: 0, fontSize: "13px", color: "#718096" }}>
                          {idioma?.sigla || 'Sin sigla'}
                        </p>
                      </div>
                      <span
                        style={{
                          padding: "3px 8px",
                          fontSize: "10px",
                          borderRadius: "10px",
                          background: "#f0fff4",
                          color: "#38a169",
                          border: "1px solid #c6f6d5",
                          fontWeight: "500",
                        }}
                      >
                        Activo
                      </span>
                    </div>
                    

                  </div>
                  ))}
                </div>
              </>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "24px 20px",
                  border: "2px dashed #e2e8f0",
                  borderRadius: "10px",
                  background: "#f7fafc",
                }}
              >
                <div style={{ fontSize: "36px", marginBottom: "12px", opacity: "0.6" }}>🌍</div>
                <h4 style={{ margin: "0 0 6px 0", fontSize: "16px", fontWeight: "600", color: "#4a5568" }}>
                  No hay idiomas registrados
                </h4>
                <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#718096", maxWidth: "280px" }}>
                  Este estudiante aún no tiene idiomas asignados en el sistema.
                </p>
                <button
                  type="button"
                  onClick={handleAbrirAgregarIdioma}
                  style={{
                    padding: "6px 12px",
                    background: "#667eea",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#5a67d8";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#667eea";
                  }}
                >
                  ✨ Agregar Idioma
                </button>
              </div>
            )}
          </div>

          {/* Modal/Formulario para Agregar Idioma */}
          {mostrarAgregarIdioma && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  handleCerrarAgregarIdioma();
                }
              }}
            >
              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "30px",
                  maxWidth: "600px",
                  width: "90%",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "600", color: "#2d3748" }}>
                    Agregar Nuevo Idioma 🌍
                  </h2>
                  <button
                    onClick={handleCerrarAgregarIdioma}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "24px",
                      cursor: "pointer",
                      color: "#718096",
                      padding: "0",
                      width: "30px",
                      height: "30px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ×
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  {/* Selección de Idioma */}
                  <div>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#4a5568" }}>
                      Idioma *
                    </label>
                    <select
                      value={idIdiomaSeleccionado || ""}
                      onChange={(e) => {
                        const valor = e.target.value;
                        if (valor) {
                          handleSeleccionarIdioma(valor);
                        } else {
                          setIdIdiomaSeleccionado(null);
                          setIdNivelSeleccionado(null);
                          setIdModuloSeleccionado(null);
                          setNiveles([]);
                          setModulos([]);
                        }
                      }}
                      style={inputStyle}
                    >
                      <option value="">Selecciona un idioma</option>
                      {Array.isArray(idiomasDisponibles) && idiomasDisponibles.length > 0 ? (
                        idiomasDisponibles
                          .filter(idioma => {
                            if (!idioma || !idioma.idIdioma) return false;
                            if (!Array.isArray(idiomasEstudiante) || idiomasEstudiante.length === 0) return true;
                            return !idiomasEstudiante.some(ie => 
                              ie && ie.idIdioma && String(ie.idIdioma) === String(idioma.idIdioma)
                            );
                          })
                          .map((idioma) => (
                            <option key={idioma.idIdioma} value={idioma.idIdioma}>
                              {idioma.nombre || 'Sin nombre'} {idioma.sigla ? `(${idioma.sigla})` : ''}
                            </option>
                          ))
                      ) : (
                        <option value="" disabled>Cargando idiomas...</option>
                      )}
                    </select>
                  </div>

                  {/* Selección de Nivel */}
                  {idIdiomaSeleccionado && Array.isArray(niveles) && niveles.length > 0 && (
                    <div>
                      <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#4a5568" }}>
                        Nivel *
                      </label>
                      <select
                        value={idNivelSeleccionado || ""}
                        onChange={(e) => {
                          const valor = e.target.value;
                          if (valor) {
                            handleSeleccionarNivel(valor);
                          } else {
                            setIdNivelSeleccionado(null);
                            setIdModuloSeleccionado(null);
                            setModulos([]);
                          }
                        }}
                        style={inputStyle}
                      >
                        <option value="">Selecciona un nivel</option>
                        {niveles.map((nivel) => (
                          <option key={nivel.idNivel} value={nivel.idNivel}>
                            {nivel.nombre || 'Sin nombre'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {idIdiomaSeleccionado && (!Array.isArray(niveles) || niveles.length === 0) && (
                    <div style={{ padding: "12px", background: "#f0f0f0", borderRadius: "8px", color: "#666" }}>
                      Cargando niveles...
                    </div>
                  )}

                  {/* Selección de Módulo */}
                  {idNivelSeleccionado && Array.isArray(modulos) && modulos.length > 0 && (
                    <div>
                      <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#4a5568" }}>
                        Módulo *
                      </label>
                      <select
                        value={idModuloSeleccionado || ""}
                        onChange={(e) => setIdModuloSeleccionado(e.target.value || null)}
                        style={inputStyle}
                      >
                        <option value="">Selecciona un módulo</option>
                        {modulos.map((modulo) => (
                          <option key={modulo.idModulo} value={modulo.idModulo}>
                            {modulo.nombre || 'Sin nombre'} {modulo.sigla ? `(${modulo.sigla})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {idNivelSeleccionado && (!Array.isArray(modulos) || modulos.length === 0) && (
                    <div style={{ padding: "12px", background: "#f0f0f0", borderRadius: "8px", color: "#666" }}>
                      Cargando módulos...
                    </div>
                  )}

                  {/* Selección de Programa (Opcional) */}
                  {idIdiomaSeleccionado && Array.isArray(programas) && programas.length > 0 && (
                    <div>
                      <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#4a5568" }}>
                        Programa (Opcional)
                      </label>
                      <select
                        value={idProgramaSeleccionado || ""}
                        onChange={(e) => setIdProgramaSeleccionado(e.target.value || null)}
                        style={inputStyle}
                      >
                        <option value="">Sin programa específico</option>
                        {programas.map((programa) => (
                          <option key={programa.idPrograma} value={programa.idPrograma}>
                            {programa.nombre || 'Sin nombre'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
                  <button
                    type="button"
                    onClick={handleCerrarAgregarIdioma}
                    style={{
                      ...buttonStyle,
                      backgroundColor: "#e53e3e",
                      color: "white",
                    }}
                    disabled={agregandoIdioma}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAgregarIdioma}
                    disabled={!idIdiomaSeleccionado || !idModuloSeleccionado || agregandoIdioma}
                    style={{
                      ...buttonStyle,
                      backgroundColor: idIdiomaSeleccionado && idModuloSeleccionado && !agregandoIdioma ? "#667eea" : "#cbd5e0",
                      color: "white",
                      cursor: idIdiomaSeleccionado && idModuloSeleccionado && !agregandoIdioma ? "pointer" : "not-allowed",
                    }}
                  >
                    {agregandoIdioma ? "Agregando..." : "Agregar Idioma"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "15px" }}>
          <button type="button" style={cancelButtonStyle} onClick={handleCancel}>
            Cancelar
          </button>
          <button type="submit" style={saveButtonStyle} disabled={isSubmitting}>
            {isSubmitting ? "Guardando..." : "Guardar Cambios"}
          </button>
        </div>
      </form>
    </ContenedorPageTemplate>
  );
};  